import { useTranslation } from 'react-i18next';
import '@/utils/notifications';
import client from '@/apolloClient';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import type { ProgramFormData } from '../shared/ProgramFormConfig';
import ProgramFormContainer from '../shared/ProgramFormContainer';
import { UpdateProgramMutation } from './UpdateProgram.graphql';
import type { UpdateProgramMutationVariables } from './UpdateProgram.graphql';

export interface UpdateProgramProps {
    open: boolean;
    program?: ProgramListItemDataFragment;
    onClose: () => void;
    onCreated: () => void;
}

const EditProgram = ({ open, onClose, onCreated, program }: UpdateProgramProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit = async (formData: ProgramFormData) => {
        const { name, description } = formData;
        try {
            const variables: UpdateProgramMutationVariables = {
                id: program?.id || '',
                input: { name, description },
            };

            const { data } = await client.mutate({
                mutation: UpdateProgramMutation,
                variables,
            });

            if (data?.updateProgram?.__typename === 'UpdateProgramSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.updateTitle'),
                    description: t('notifications.updateDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.updateProgram?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.updateProgram.fields,
                };
                showErrorNotification({
                    message: t('notifications.updateErrorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.updateErrorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error updating program');
        } catch (error) {
            console.error('Error updating program:', error);
            // Handle error appropriately
        }
    };
    return (
        <ProgramFormContainer
            mode="edit"
            program={program}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={t('modalEditProgram.title')}
        />
    );
};

export default EditProgram;
